import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck, Copy, Lock, Smartphone } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";

export default function SecurityPage() {
  const [status, setStatus] = useState(null);
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/auth/backup-codes/status"); setStatus(data); } catch { /* */ } };
  useEffect(() => { load(); }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error("The two new passwords do not match."); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  const generate = async () => {
    if (status?.generated && !window.confirm("Regenerate codes? Any previous codes will stop working.")) return;
    setBusy(true);
    try { const { data } = await api.post("/auth/backup-codes/generate"); setCodes(data.codes); toast.success("New backup codes generated — save them now"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyAll = () => { navigator.clipboard.writeText((codes || []).join("\n")); toast.success("Copied to clipboard"); };

  return (
    <div data-testid="security-page" className="max-w-xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight mb-1">Account security</h1>
        <p className="text-sm text-slate-500">Change your password and manage your 2FA backup codes.</p>
      </div>

      <form onSubmit={changePassword} data-testid="change-password-form" className="border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3"><Lock className="w-5 h-5 text-[#B01E28]" /><span className="font-display font-bold">Change password</span></div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Current password</Label>
            <Input data-testid="current-password" type="password" required value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">New password</Label>
            <Input data-testid="cp-new-password" type="password" required value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Confirm new password</Label>
            <Input data-testid="cp-confirm-password" type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" />
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2 font-mono">Min 12 characters · checked against a breached-password list.</p>
        <Button data-testid="change-password-button" type="submit" disabled={pwBusy || pw.next.length < 12 || pw.confirm.length < 12} className="rounded-lg bg-[#1F1F1F] text-white mt-4">
          {pwBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}Update password
        </Button>
      </form>

      <div className="border border-slate-200 bg-white p-5">
        <p className="text-sm text-slate-500 mb-3 -mt-1">2FA backup codes — your lifeline if you lose your authenticator app.</p>
        <div className="flex items-center gap-2 mb-2"><KeyRound className="w-5 h-5 text-[#B01E28]" /><span className="font-display font-bold">Backup codes</span></div>
        <p className="text-xs text-slate-600 mb-3">Each code works <b>once</b>, in place of your 6-digit authenticator code, at login. Store them somewhere safe and offline.</p>
        {status && (
          <div data-testid="codes-status" className="font-mono text-xs mb-3 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            {status.generated ? `${status.remaining} of ${status.total} codes remaining` : "No backup codes generated yet"}
          </div>
        )}

        {/* A used backup code is the emergency route into this account having been taken. His
            ruling 2026-10-01, after finding one of ten used with nothing able to say when:
            answerable in one glance. Codes used before this was tracked say so plainly instead of
            inventing a date. */}
        {status?.used_count > 0 && (
          <div data-testid="codes-last-used" className="text-xs mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-amber-900">
            {status.last_used_at
              ? <>Last used <b>{String(status.last_used_at).slice(0, 16).replace("T", " ")}</b>
                  {status.last_used_ip ? <> from <span className="font-mono">{status.last_used_ip}</span></> : null}.</>
              : <><b>{status.used_count === 1 ? "One code has been used" : `${status.used_count} codes have been used`}</b> — date not recorded (used before this was tracked).</>}
            {status.used_without_stamp > 0 && status.last_used_at
              ? <> {status.used_without_stamp} earlier use(s) have no recorded date.</> : null}
            <div className="mt-1 text-[11px]">If that wasn't you, regenerate below — it invalidates every old printed code.</div>
          </div>
        )}

        {codes && (
          <div data-testid="codes-list" className="bg-slate-900 text-emerald-300 font-mono text-sm p-4 grid grid-cols-2 gap-2 mb-3">
            {codes.map((c, i) => <div key={i}>{c}</div>)}
          </div>
        )}
        {codes && <Button data-testid="copy-codes" variant="outline" size="sm" className="rounded-lg border-slate-200 mb-3 mr-2" onClick={copyAll}><Copy className="w-3.5 h-3.5 mr-1" />Copy all</Button>}

        <div>
          <Button data-testid="generate-codes" onClick={generate} disabled={busy} className="rounded-lg bg-[#1F1F1F] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <KeyRound className="w-4 h-4 mr-1" />}
            {status?.generated ? "Regenerate codes" : "Generate 10 codes"}
          </Button>
        </div>
        {codes && <p className="text-[11px] text-amber-600 mt-3">⚠ These are shown only once. They will not be displayed again after you leave this page.</p>}
      </div>

      <TrustedDevices />
    </div>
  );
}

/* DEVICES YOU TRUST — they scope the lockout counter, they don't skip a single check.
   A device you've signed in on counts its own failed attempts on its own counter, so somebody
   guessing your password from their machine can never lock you out of yours. It still locks at
   five failures on that device, and it never skips your password or your 6-digit code. */
function TrustedDevices() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data: d } = await api.get("/auth/trusted-devices"); setData(d); }
    catch { setData({ items: [] }); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (d) => {
    if (!window.confirm(`Stop trusting ${d.device}${d.this_device ? " (this device)" : ""}? Failed attempts from it will count on the shared counter again.`)) return;
    setBusy(true);
    try { await api.post(`/auth/trusted-devices/${d.id}/revoke`); toast.success("Device no longer trusted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Stop trusting every device except this one? Use this if you've lost a phone or a laptop.")) return;
    setBusy(true);
    try { const { data: r } = await api.post("/auth/trusted-devices/revoke-others"); toast.success(r.revoked ? `${r.revoked} device(s) no longer trusted` : "No other devices were trusted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const items = data?.items || [];
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid="trusted-devices-card">
      <div className="flex items-center gap-2 mb-2"><Smartphone className="w-5 h-5 text-[#B01E28]" /><span className="font-display font-bold">Devices you trust</span></div>
      <p className="text-xs text-slate-600 mb-3">
        A device you've signed in on keeps its <b>own</b> failed-attempt counter — so somebody guessing your
        password from somewhere else can't lock you out of your own phone or PC. It is <b>not</b> a shortcut:
        that device still locks after five failures, and it never skips your password or your 6-digit code.
        Trust lapses after {data?.idle_days || 60} days without use.
      </p>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="no-trusted-devices">No trusted devices yet — this one is added the next time you sign in.</p>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg mb-3" data-testid="trusted-devices-list">
          {items.map((d) => (
            <div key={d.id} data-testid={`trusted-device-${d.id}`} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-2">
                  {d.device}
                  {d.this_device && <span data-testid="this-device-chip" className="rounded px-1.5 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-800">THIS DEVICE</span>}
                </div>
                <div className="font-mono text-[10px] text-slate-500">
                  last used {formatDateTime(d.last_used)} · trusted until {String(d.trusted_until || "").slice(0, 10)}
                </div>
              </div>
              <Button data-testid={`revoke-device-${d.id}`} size="sm" variant="outline" disabled={busy}
                className="rounded-lg h-7 border-slate-200 text-destructive shrink-0" onClick={() => revoke(d)}>
                Stop trusting
              </Button>
            </div>
          ))}
        </div>
      )}
      {items.length > 1 && (
        <Button data-testid="revoke-other-devices" variant="outline" size="sm" disabled={busy}
          className="rounded-lg border-slate-200" onClick={revokeOthers}>
          Stop trusting every other device
        </Button>
      )}
    </div>
  );
}
